import {
  AppState,
  postPolicyApplicationFormPrefillRequest,
  selectPrefillDataForClient,
} from '@client/model';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useClientPolicyApplication } from './useClientPolicyApplication';
import { useParams } from 'react-router-dom';

export const usePrefillDataForForm = () => {
  const { clientId, clientPolicyApplicationId, formId, forClientId } = useParams();
  const { steps, underwritingFormId, applicationFormId } = useClientPolicyApplication();
  const dispatch = useDispatch();

  // Determine current step based on URL params
  const currentStep = useMemo(() => {
    if (formId === applicationFormId) {
      return steps?.find(step => step.stepType === 'application-form');
    }
    if (formId === underwritingFormId) {
      if (forClientId === clientId) {
        return steps?.find(step => step.stepType === 'pre-underwriting-form');
      }
      return steps?.find(step => step.stepType === 'partner-pre-underwriting-form');
    }

    return undefined;
  }, [formId, applicationFormId, underwritingFormId, forClientId, clientId, steps]);

  const prefillData = useSelector((state: AppState) =>
    selectPrefillDataForClient(
      state,
      forClientId ?? '',
      clientPolicyApplicationId ?? '',
      formId ?? '',
    ),
  );

  // Save prefill data to database if needed
  const savePrefillData = useCallback(() => {
    if (!clientId || !currentStep?.clientPolicyApplicationForm) {
      return;
    }

    const { clientPolicyApplicationFormId } = currentStep.clientPolicyApplicationForm;

    if (!clientPolicyApplicationFormId) {
      return;
    }

    if (prefillData?.prefillData && prefillData?.citations) {
      dispatch(
        postPolicyApplicationFormPrefillRequest({
          clientId,
          clientPolicyApplicationFormId,
          prefillAnswers: prefillData.prefillData,
          prefillCitations: prefillData.citations,
        }),
      );
    }
  }, [
    clientId,
    currentStep?.clientPolicyApplicationForm?.clientPolicyApplicationFormId,
    prefillData,
    dispatch,
  ]);

  // Determine if we should use prefill data
  const shouldUsePrefill = useMemo(() => {
    if (!currentStep) return false;

    return (
      currentStep.stepType === 'pre-underwriting-form' ||
      currentStep.stepType === 'partner-pre-underwriting-form' ||
      currentStep.stepType === 'application-form'
    );
  }, [currentStep?.stepType]);

  const dataToReturn = useMemo(() => {
    if (!shouldUsePrefill) {
      return { prefillData: [], citations: [] };
    }

    // Check if data exists in the current step
    const stepForm = currentStep?.clientPolicyApplicationForm;
    const stepAnswers = stepForm?.prefillAnswers?.answers;
    const stepCitations = stepForm?.prefillCitations?.citations;

    if (
      stepAnswers !== undefined &&
      stepCitations !== undefined &&
      Array.isArray(stepAnswers) &&
      Array.isArray(stepCitations) &&
      stepAnswers.length > 0 &&
      stepCitations.length > 0
    ) {
      return {
        prefillData: stepAnswers,
        citations: stepCitations,
      };
    }

    if (
      currentStep?.clientPolicyApplicationForm &&
      forClientId &&
      prefillData?.prefillData &&
      prefillData?.citations
    ) {
      savePrefillData();

      return {
        prefillData: prefillData.prefillData,
        citations: prefillData.citations,
      };
    }

    return { prefillData: [], citations: [] };
  }, [
    shouldUsePrefill,
    currentStep?.clientPolicyApplicationForm?.clientPolicyApplicationFormId,
    forClientId,
    prefillData,
    savePrefillData,
  ]);

  return dataToReturn;
};
